/* eslint-disable @typescript-eslint/no-explicit-any */
import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpStatusCode } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { ComponentType } from "@angular/cdk/overlay";
import { Observable, catchError, combineLatest, first, switchMap, throwError } from "rxjs";
import { FromRouter, RootState } from "../../store";
import { Store } from "@ngrx/store";
import { Router } from "@angular/router";
import { AppRoutes, NOT_FOUND_RESOURCES } from "src/app/constants";
import { MatDialog } from "@angular/material/dialog";
import { MergeAccountComponent } from "src/app/modals/merge-account/merge-account.component";

interface NotFoundInterceptorCall {
  page: string;
  apiCallUrl: string;
  dialog?: ComponentType<any>;
}

const NOT_FOUND_INTERCEPTOR_CALLS: NotFoundInterceptorCall[] = [
  { page: "/:programId/coupons/:couponId/*", apiCallUrl: "/coupons/:couponId" },
  { page: "/:programId/workflows/:workflowId/*", apiCallUrl: "/workflow-service/v2/workflows/:workflowId" },
  { page: "/:programId/admins/:adminId", apiCallUrl: "/user-app-service/keycloak/users/:adminId" },
  { page: "/:programId/settings/endpoints/:endpointId", apiCallUrl: "/programs/:programId/endpoints/:endpointId" },
  {
    page: "/:programId/settings/custom-properties/:customPropertyId",
    apiCallUrl: "/programs/:programId/customproperties/:customPropertyId",
  },
  {
    page: "/:programId/posts/:postId",
    apiCallUrl: "/newsfeed-service/posts/:postId",
  },
  {
    page: "/:programId/settings/tags/:tagId",
    apiCallUrl: "/programs/:programId/tags/:tagId",
  },
  {
    page: "/:programId/settings/interaction-points/:interactionPointId",
    apiCallUrl: "/programs/:programId/interactionpoints/:interactionPointId",
  },
  {
    page: "/:programId/statuslevel/:statusLevelId",
    apiCallUrl: "/programs/:programId/statuslevelsets/:statusLevelId",
  },
  {
    page: "/:programId/partner/:partnerId/stores/:storeId/*",
    apiCallUrl: "/programs/:programId/partners/:partnerId/stores/:storeId",
  },
  {
    page: "/:programId/partner/:partnerId/*",
    apiCallUrl: "/programs/:programId/partners/:partnerId",
  },
  {
    page: "/:programId/webhooks/:webhookId/*",
    apiCallUrl: "/programs/:programId/webhooks/action-config/:webhookId",
  },
  { page: "/:programId/customer-service2/customer-detail/*", apiCallUrl: "/users/:userId" },
  {
    page: "/:programId/customer-service2/customer-detail/*",
    apiCallUrl: "/v2/accountdetails/:accountId",
    dialog: MergeAccountComponent,
  },
];

@Injectable({ providedIn: "root" })
export class NotFoundInterceptor implements HttpInterceptor {
  private readonly callsToIntercept = NOT_FOUND_INTERCEPTOR_CALLS;
  private readonly store = inject(Store<RootState>);
  private readonly router = inject(Router);
  private readonly dialog = inject(MatDialog);

  private readonly url$: Observable<string> = this.store.select(FromRouter.selectUrl);
  private readonly programId$: Observable<string> = this.store.select(FromRouter.selectProgramId);

  public intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const shouldIntercept = this.callsToIntercept.some(call => {
      const url = new URL(req.url, window.location.origin);
      const apiPattern = `^${call.apiCallUrl.replace(/:\w+/g, "[^/]+")}$`;
      const apiMatches = new RegExp(apiPattern).test(url.pathname);

      const pagePattern = `^${call.page.replace(/:\w+/g, "[^/]+").replace(/\*/g, ".*")}$`;
      const pageMatches = new RegExp(pagePattern).test(window.location.pathname);

      let dialogMatches = false;
      if (call.dialog) dialogMatches = this.dialog.openDialogs.some(dialog => dialog.componentInstance instanceof (call.dialog as any));

      return apiMatches && pageMatches && !dialogMatches;
    });

    if (!shouldIntercept) return next.handle(req);

    return combineLatest([this.programId$, this.url$]).pipe(
      first(),
      switchMap(([programId, url]) =>
        next.handle(req).pipe(
          catchError((error: HttpErrorResponse) => {
            if (error.status === HttpStatusCode.NotFound.valueOf()) {
              void this.router.navigate([AppRoutes.root, programId, AppRoutes.notFound], {
                queryParams: { resource: this.getResource(url) },
              });
            }

            // eslint-disable-next-line @typescript-eslint/no-unsafe-return
            return throwError(() => error.error);
          }),
        ),
      ),
    );
  }

  private getResource(url: string): AppRoutes | undefined {
    return NOT_FOUND_RESOURCES.find(route => url.includes(route));
  }
}
